
/**
 * Returns a Promise that iterates serially over an array of elements converting them to Promises on-demand
 * via the mapFunc and returning an array of all results upon completion.
 *
 * This function allows Promises to be built on-demand using the elements and mapFunc as the function iterates
 * over each array entry. The continueFunc allows you to decide upon resolving a Promise if you want to
 * continue iterating or return results now.
 *
 * @param {Array} elements An array of values. Each value will be passed to the mapFunc as serial iteration occurs.
 * @param {Function} mapFunc A function executed for each serial iteration receiving the arg at the index.
 * The function can return a value or a Promise that will be treated as it's result for the iteration.
 * @param {Function} continueFunc A function executed once each iteration is resolved. The result of the
 * iteration is passed in as an argument, and the function must return true|false. true indicates that
 * it will continue to iterate, false will break the iteration.
 * @returns A Promise the resolves to an array of all of the values resolved from each iteration.
 */
function serial(elements, mapFunc = element => element , continueFunc = () => true) {
	return new Promise((resolve) => {
		let results = [];

		let mapFuncToPromise = (arg, mapFunc) => {
			let r = mapFunc(arg);
			if (!r.then) r = Promise.resolve(r);
			return r;
		};

		let iterate = () => mapFuncToPromise(elements[results.length], mapFunc)
			.then(result => {
				results.push(result);
				return results.length < elements.length && continueFunc(result);
			})
			.then(iterateAgain => iterateAgain ? iterate() : resolve(results));

		iterate();
	});
}

/**
 * Builds Promises resolving to a boolean for each iteration of the elements array via mapFunc. As long as
 * each iteration keeps resolving to true, the iteration will continue. If an iteration resolves to false,
 * the iteration will cease and the result will be returned.
 *
 * @param {Array} elements An array of values. Each value will be passed to the mapFunc as serial iteration occurs.
 * @param {Function} mapFunc Must return a boolean or a Promise that resolves to a boolean.
 * @returns A Promise that resolves true if all iterations resulted in a true resolution.
 */
export function serialAND(elements, mapFunc) {
	return serial(elements, mapFunc, result => !!result)
		.then(results => !results.some(r => r === false));
}

/**
 * Builds Promises resolving to a boolean for each iteration of the elements array via mapFunc. As long as
 * each iteration keeps resolving to false, the iteration will continue. If an iteration resolves to true,
 * the iteration will cease and a true result will be returned.
 *
 * @param {Array} elements An array of values. Each value will be passed to the mapFunc as serial iteration occurs.
 * @param {Function} mapFunc Must return a boolean or a Promise that resolves to a boolean.
 * @returns A Promise that resolves true if one of the iterations resulted in a true resolution.
 */
export function serialOR(elements, mapFunc) {
	return serial(elements, mapFunc, result => !result)
		.then(results => results.some(r => r === true));
}